import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalSpaceChrome, {
	PortalBrandLockup,
} from '../../components/Portal/PortalSpaceChrome';
import { SpaceChip, AvatarStack } from '../../components/Portal/spaceVisuals';
import { getReachableSpaces } from '../../components/Portal/portalSpaces';

/* The owner hub at /portal — a permission-aware pulse board. One card per
   reachable space, each with the space's colour + icon signature and a single
   cheap summary count. Phase B ships empty, so the zero-state is the real
   launch state: calm "all clear", never an error or a void.

   Only owners reach this screen — a single-space user is redirected straight
   into their dashboard upstream (PortalSpaceEntry), so the hub never renders a
   one-tile picker. */

// `populated` is wired false for Phase B (no Phase D data yet); the populated
// branch is kept ready so the same card lights up the moment counts go live.
const HUB_POPULATED = false;

function HubCard({ space, populated }) {
	const s = space.summary;
	return (
		<Link
			to={space.route}
			className='psp-card'
			style={{ '--psp-accent': `var(--space-${space.key})` }}>
			<span className='psp-card__bar' />
			<div className='psp-card__head'>
				<SpaceChip space={space} size={44} radius={13} />
				<div className='psp-card__titles'>
					<div className='psp-card__name'>{space.name}</div>
					<div className='psp-card__sub'>{space.sub}</div>
				</div>
				<i className='fa-sharp fa-solid fa-chevron-right psp-card__chev' />
			</div>
			<div className='psp-card__foot'>
				<div className='psp-card__count'>
					{populated && s ? (
						<>
							<div className='psp-card__count-row'>
								<span className='psp-card__count-num'>
									{s.count}
								</span>
								<span className='psp-card__count-label'>
									{s.label}
								</span>
							</div>
							{s.extra && (
								<div className='psp-card__extra'>
									<i className='fa-sharp fa-solid fa-calendar-day' />
									{s.extra}
								</div>
							)}
						</>
					) : (
						<div className='psp-card__zero'>
							<span className='psp-card__zero-check'>
								<i className='fa-sharp fa-solid fa-check' />
							</span>
							{s?.zero || 'All clear'}
						</div>
					)}
				</div>
				<AvatarStack members={space.members} size={22} />
			</div>
		</Link>
	);
}

function PortalHub() {
	const { currentUser, userProfile } = useAuth();
	const spaces = getReachableSpaces(userProfile, currentUser?.email);

	const anyDue = HUB_POPULATED;

	return (
		<PortalSpaceChrome leading={<PortalBrandLockup />}>
			<div className='psp-page'>
				<div className='psp-hub__greeting'>
					<div className='psp-hub__eyebrow'>Your spaces</div>
					<h1 className='psp-hub__title'>Welcome back</h1>
					<p className='psp-hub__sub'>
						{anyDue
							? 'A few things need you today.'
							: 'Nothing needs you right now.'}
					</p>
				</div>

				<div className='psp-hub__grid'>
					{spaces.map((space) => (
						<HubCard
							key={space.key}
							space={space}
							populated={HUB_POPULATED}
						/>
					))}
				</div>
			</div>
		</PortalSpaceChrome>
	);
}

export default PortalHub;
